export const state = () => ({
  distributionLists: [],
  activeDistributionList: null,
  distributionListSearchParams: {
    name: '',
    participant: ''
  },
  distributionListCount: 0,
  distributionListFilterOptions: {
    status: 'all',
    sortDirection: 'asc',
    listLimit: '20',
    listSkip: '',
    searchTerm: ''
  }
})

export const getters = {
  getDistributionLists: state => state.distributionLists,
  getActiveDistributionList: state => state.activeDistributionList,
  getDistributionListCount: state => state.distributionListCount,
  getDistributionListSearchParams: state => state.distributionListSearchParams,
  getDistributionListFilterOptions: state => state.distributionListFilterOptions,
  getDefaultListLimit: state => parseInt(state.distributionListFilterOptions.listLimit) || 20
}

export const actions = {
  async fetchDistributionLists({ commit, dispatch, state }, { searchParams, limit, skip, sortby, append = false } = {}) {
    const params = []

    // Use default limit from filterOptions if not provided
    const defaultLimit = parseInt(state.distributionListFilterOptions.listLimit) || 20
    const actualLimit = limit !== undefined ? limit : defaultLimit

    // Add individual search parameters
    if (searchParams) {
      if (searchParams.name) { params.push(`name=${encodeURIComponent(searchParams.name)}`) }
      if (searchParams.participant) { params.push(`participant=${encodeURIComponent(searchParams.participant)}`) }
    }

    // Add other parameters
    params.push(`limit=${actualLimit}`)
    if (skip) { params.push(`skip=${skip}`) }
    if (sortby) { params.push(`sortby=${sortby}`) }

    try {
      const response = await this.$axios.$get('/distribution-lists?' + params.join('&'))

      // Handle both response formats: direct array or object with properties
      let distributionLists, distributionListCount
      if (Array.isArray(response)) {
        distributionLists = response
        distributionListCount = response.length
      } else {
        distributionLists = response.distributionLists || []
        distributionListCount = response.count || 0
      }

      if (append) {
        commit('appendDistributionLists', distributionLists)
      } else {
        commit('setDistributionLists', distributionLists)
      }
      commit('setDistributionListCount', distributionListCount)
      return distributionLists
    } catch (err) {
      return dispatch(
        'global/setNetworkError',
        { method: 'distributionLists/fetchDistributionLists', info: err },
        { root: true }
      )
    }
  },

  async fetchDistributionListById({ commit, dispatch }, id) {
    try {
      const distributionList = await this.$axios.$get(`/distribution-lists/${id}`)
      commit('setActiveDistributionList', distributionList)
      return distributionList
    } catch (err) {
      return dispatch(
        'global/setNetworkError',
        { method: 'distributionLists/fetchDistributionListById', info: err },
        { root: true }
      )
    }
  },

  async createDistributionList({ dispatch }, distributionList) {
    try {
      const newDistributionList = await this.$axios.$post('/distribution-lists/create', distributionList)
      return newDistributionList
    } catch (err) {
      return dispatch(
        'global/setNetworkError',
        { method: 'distributionLists/createDistributionList', info: err },
        { root: true }
      )
    }
  },

  async copyDistributionList({ dispatch }, { distributionListId, name }) {
    try {
      const payload = name ? { name } : {}
      const result = await this.$axios.$post(`/distribution-lists/${distributionListId}/copy`, payload)
      return result
    } catch (err) {
      return dispatch(
        'global/setNetworkError',
        { method: 'distributionLists/copyDistributionList', info: err },
        { root: true }
      )
    }
  },

  async updateDistributionListById({ dispatch }, { id, distributionList }) {
    try {
      const updatedDistributionList = await this.$axios.$put(`/distribution-lists/${id}`, distributionList)
      return updatedDistributionList
    } catch (err) {
      return dispatch(
        'global/setNetworkError',
        { method: 'distributionLists/updateDistributionListById', info: err },
        { root: true }
      )
    }
  },

  async deleteDistributionList({ dispatch }, id) {
    try {
      const result = await this.$axios.$delete(`/distribution-lists/${id}`)
      return result.result === 'ok'
    } catch (err) {
      return dispatch(
        'global/setNetworkError',
        { method: 'distributionLists/deleteDistributionList', info: err },
        { root: true }
      )
    }
  },

  async addParticipantToDistributionList({ commit, dispatch }, { distributionListId, participantId, participant, role }) {
    try {
      const payload = {}

      if (participantId) {
        // Adding existing participant
        payload.participantId = participantId
      } else if (participant) {
        // Creating new participant
        payload.participant = participant
      }

      if (role) {
        payload.role = role
      }

      const result = await this.$axios.$post(`/distribution-lists/${distributionListId}/participants`, payload)

      // Update the active distribution list with the updated data
      if (result && result._id) {
        commit('setActiveDistributionList', result)
      }

      return result
    } catch (err) {
      const {
        response: {
          status: statusCode = 0,
          data = {}
        } = {}
      } = err || {}

      // Return error information for the component to handle
      if (statusCode === 409) {
        return { error: true, errorType: 'conflict', message: data.message || data }
      } else if (statusCode === 400) {
        return { error: true, errorType: 'validation', message: data.message || data }
      } else if (statusCode === 404) {
        return { error: true, errorType: 'notfound', message: data.message || data }
      }

      // For other errors, still dispatch to global error handler
      return dispatch(
        'global/setNetworkError',
        { method: 'distributionLists/addParticipantToDistributionList', info: err },
        { root: true }
      )
    }
  },

  async removeParticipantFromDistributionList({ commit, dispatch }, { distributionListId, participantId }) {
    try {
      const result = await this.$axios.$delete(`/distribution-lists/${distributionListId}/participants/${participantId}`)

      // Update the active distribution list with the updated data
      if (result && result._id) {
        commit('setActiveDistributionList', result)
      }

      return result
    } catch (err) {
      return dispatch(
        'global/setNetworkError',
        { method: 'distributionLists/removeParticipantFromDistributionList', info: err },
        { root: true }
      )
    }
  },

  async updateParticipantRole({ commit, dispatch }, { distributionListId, participantId, role }) {
    try {
      const result = await this.$axios.$put(`/distribution-lists/${distributionListId}/participants/${participantId}/role`, {
        role
      })

      // Update the active distribution list with the updated data
      if (result && result._id) {
        commit('setActiveDistributionList', result)
      }

      return result
    } catch (err) {
      return dispatch(
        'global/setNetworkError',
        { method: 'distributionLists/updateParticipantRole', info: err },
        { root: true }
      )
    }
  },

  async importParticipantsToDistributionList({ commit, dispatch }, { distributionListId, file }) {
    try {
      const data = new FormData()
      data.append('participants', file)
      const response = await this.$axios.$post(`/distribution-lists/${distributionListId}/participants/import`, data, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })

      // Handle both new and legacy formats
      if (response.errorReport) {
        // New standardized format - return as-is
        return response
      } else {
        // Legacy format - extract values and set errors in store
        const {
          importCount = 0,
          importTotal = 0,
          errorCount = 0,
          participantImportErrors = []
        } = response

        commit('participants/setImportedParticipantErrors', participantImportErrors, { root: true })
        return { importCount, errorCount, importTotal, participantImportErrors }
      }
    } catch (err) {
      return dispatch(
        'global/setNetworkError',
        { method: 'distributionLists/importParticipantsToDistributionList', info: err },
        { root: true }
      )
    }
  },

  setDistributionListFilter({ commit }, filter) {
    commit('setDistributionListFilter', filter)
  },

  setDefaultListLimit({ commit }, limit) {
    commit('setDefaultListLimit', limit)
  },

  resetDistributionListSearchParams({ commit }) {
    commit('setDistributionListSearchParams', {
      name: '',
      participant: ''
    })
  }
}

export const mutations = {
  setDistributionLists(state, distributionLists) {
    state.distributionLists = distributionLists
  },

  appendDistributionLists(state, distributionLists) {
    state.distributionLists = [...state.distributionLists, ...distributionLists]
  },

  setDistributionListCount(state, count) {
    state.distributionListCount = count
  },

  setActiveDistributionList(state, distributionList) {
    state.activeDistributionList = distributionList
  },

  setDistributionListSearchParams(state, searchParams) {
    state.distributionListSearchParams = searchParams
  },

  setDistributionListFilter(state, filter) {
    state.distributionListFilterOptions.searchTerm = filter
  },

  setDefaultListLimit(state, limit) {
    state.distributionListFilterOptions.listLimit = limit.toString()
  }
}
